import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { PRIORITY_META, CONTROL_STATUS_META } from "../contexts/ComplianceContext";
import { ArrowLeft, ShieldCheck, User, Calendar, FileText, ChevronRight } from "lucide-react";
import { format } from "date-fns";

export default function ControlDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ctrl, fws] = await Promise.all([
        api.get(`/compliance/controls/${id}`),
        api.get("/compliance/frameworks"),
      ]);
      setC(ctrl.data);
      setFrameworks(fws.data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="h-40 bg-muted animate-pulse rounded" />;
  if (!c) return <div>Control not found</div>;

  const s = CONTROL_STATUS_META[c.status] || { label: c.status, variant: "neutral" };
  const p = PRIORITY_META[c.priority] || { label: c.priority, variant: "neutral" };

  return (
    <div className="space-y-6">
      <Link
        to="/controls"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" /> All controls
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <h1 className="text-2xl font-semibold tracking-tight">{c.title}</h1>
                <div className="flex gap-2">
                  <Badge variant={p.variant}>{p.label}</Badge>
                  <Badge variant={s.variant}>{s.label}</Badge>
                </div>
              </div>
              {c.description && (
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{c.description}</p>
              )}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="label-eyebrow">Owner</div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                    {c.owner_name || "—"}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Department</div>
                  <div className="mt-1">{c.department || "—"}</div>
                </div>
                <div>
                  <div className="label-eyebrow">Frequency</div>
                  <div className="mt-1 flex items-center gap-1.5 capitalize">
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    {c.assessment_frequency?.replace("_", " ")}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Evidence Required</div>
                  <div className="mt-1">{c.evidence_required ? "Yes" : "No"}</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="control-mappings-card">
          <CardHeader>
            <CardTitle>Framework mappings</CardTitle>
            <CardDescription>
              This single control satisfies {c.mappings?.length || 0} framework{c.mappings?.length !== 1 ? "s" : ""}.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {(c.mappings || []).map((m) => {
                const fw = frameworks.find((x) => x.id === m.framework_id);
                return (
                  <li key={`${m.framework_id}-${m.control_ref}`} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{fw?.name || "Unknown"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{m.control_ref}</div>
                    </div>
                    {fw && (
                      <Link
                        to={`/frameworks/${fw.id}`}
                        className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
                      >
                        Open framework <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                  </li>
                );
              })}
              {(c.mappings || []).length === 0 && (
                <li className="px-6 py-6 text-center text-muted-foreground text-sm">
                  No framework mappings.
                </li>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card data-testid="control-subcontrols-card">
          <CardHeader>
            <CardTitle>Sub-controls</CardTitle>
            <CardDescription>Implementation checkpoints for this control.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {(c.sub_controls || []).length === 0 && (
                <li className="px-6 py-6 text-center text-muted-foreground text-sm">
                  No sub-controls defined.
                </li>
              )}
              {(c.sub_controls || []).map((sc) => {
                const ss = CONTROL_STATUS_META[sc.status] || { label: sc.status, variant: "neutral" };
                return (
                  <li key={sc.id} className="px-6 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        <span className="font-mono text-xs text-muted-foreground mr-2">{sc.code}</span>
                        {sc.title}
                      </div>
                    </div>
                    <Badge variant={ss.variant}>{ss.label}</Badge>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="control-evidence-card">
        <CardHeader>
          <CardTitle>Linked evidence</CardTitle>
          <CardDescription>Documents demonstrating implementation of this control.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {(c.evidence || []).length === 0 && (
              <li className="px-6 py-6 text-center text-muted-foreground text-sm">
                No evidence linked yet. Upload from the{" "}
                <Link to="/evidence" className="text-primary hover:underline">
                  Evidence Repository
                </Link>
                .
              </li>
            )}
            {(c.evidence || []).map((e) => (
              <li key={e.id} className="px-6 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-3">
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {e.filename} · v{e.version}
                      {e.created_at && ` · ${format(new Date(e.created_at), "MMM d, yyyy")}`}
                    </div>
                  </div>
                </div>
                <Badge variant={e.status === "approved" ? "success" : e.status === "rejected" ? "critical" : "warning"}>
                  {e.status}
                </Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
